import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ReportUserDialog } from './ReportUserDialog';
import { UserProfileModal } from './UserProfileModal';

/**
 * Owns the dialogs the person menu opens, so they survive the menu closing.
 *
 * The problem this exists for: on the channel roster the menu lives inside a
 * `Popover`, and `PopoverContent` UNMOUNTS its children when it closes. A Radix
 * dialog portals outside the popover's DOM, so clicking inside the dialog reads
 * as an outside-interaction, closes the popover, and takes the dialog down with
 * it — the confirmation vanishes and the action silently never happens.
 *
 * Mounting the dialogs here instead, above the popover in the tree, decouples
 * their lifetime from the menu's. Every surface that renders `UserActionMenu`
 * wraps it in this provider — the roster, the guild member table, the message
 * sender popup — which also means one dialog instance exists per LIST rather
 * than one per row.
 */

export interface PersonActionTarget {
  userId: string;
  displayName: string;
  /** Channel a report should cite, when opened from one. */
  channelId?: string;
  /** Message a report should cite, when opened from one. */
  messageId?: string;
}

export interface RemoveRequest extends PersonActionTarget {
  removeLabel: string;
  onConfirm: (userId: string) => void | Promise<void>;
}

interface UserActionDialogApi {
  openProfile: (target: PersonActionTarget) => void;
  openReport: (target: PersonActionTarget) => void;
  confirmRemove: (request: RemoveRequest) => void;
}

const UserActionDialogContext = createContext<UserActionDialogApi | null>(null);

/**
 * Returns the shared dialog host. Throws when there is no provider above,
 * rather than quietly rendering a menu whose actions do nothing — a missing
 * host is a wiring mistake, and the silent version of it is exactly the failure
 * mode this component exists to prevent.
 */
export function useUserActionDialogs(): UserActionDialogApi {
  const api = useContext(UserActionDialogContext);
  if (!api) {
    throw new Error(
      'UserActionMenu must be rendered inside a <UserActionDialogProvider>. ' +
        'The provider owns the profile/report/remove dialogs so they survive the ' +
        'menu (and any popover around it) closing.',
    );
  }
  return api;
}

interface UserActionDialogProviderProps {
  /** Opens a conversation from the profile card, when the surface has one. */
  onOpenDm?: (userId: string) => void;
  children: ReactNode;
}

export function UserActionDialogProvider({ onOpenDm, children }: UserActionDialogProviderProps) {
  const [profileTarget, setProfileTarget] = useState<PersonActionTarget | null>(null);
  const [reportTarget, setReportTarget] = useState<PersonActionTarget | null>(null);
  const [removeRequest, setRemoveRequest] = useState<RemoveRequest | null>(null);

  const api = useMemo<UserActionDialogApi>(
    () => ({
      openProfile: setProfileTarget,
      openReport: setReportTarget,
      confirmRemove: setRemoveRequest,
    }),
    [],
  );

  const closeRemove = useCallback(() => setRemoveRequest(null), []);

  return (
    <UserActionDialogContext.Provider value={api}>
      {children}

      {/* Removal is destructive and cannot be undone from the menu, so it asks
          first — matching the guild roster's Kick, which has always confirmed.
          An AlertDialog, never window.confirm: a native modal fired from a
          dismissing Radix menu is silently swallowed (see
          tests/architecture/no-native-dialog-in-select-handler). */}
      <AlertDialog open={removeRequest !== null} onOpenChange={(open) => !open && closeRemove()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{removeRequest?.removeLabel ?? 'Remove'}?</AlertDialogTitle>
            <AlertDialogDescription>
              {removeRequest?.displayName} will lose access to this conversation. They can be added
              back later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const request = removeRequest;
                closeRemove();
                if (request) void request.onConfirm(request.userId);
              }}
            >
              {removeRequest?.removeLabel ?? 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {reportTarget && (
        <ReportUserDialog
          userId={reportTarget.userId}
          displayName={reportTarget.displayName}
          channelId={reportTarget.channelId}
          messageId={reportTarget.messageId}
          open
          onOpenChange={(open) => !open && setReportTarget(null)}
        />
      )}

      {/* Mounted only while open — the profile card fetches on mount, and a
          member list would otherwise fire one request per row. */}
      {profileTarget && (
        <UserProfileModal
          userId={profileTarget.userId}
          onClose={() => setProfileTarget(null)}
          onOpenDm={onOpenDm}
        />
      )}
    </UserActionDialogContext.Provider>
  );
}
